/**
 * Keeps the mobile PM container aligned with the keyboard-reduced viewport.
 * Viewport events only queue work because WebKit may emit another event while
 * a layout-affecting style is written.
 */
export function createMobileKeyboardViewport(pmContainer, scrollToBottom, browserWindow = window) {
  const MAX_FEEDBACK_UPDATES = 4;
  const SETTLE_DELAY_MS = 50;
  const viewport = browserWindow.visualViewport;
  let framePending = false;
  let frameId = null;
  let scrollTimer = null;
  let settleTimer = null;
  let settling = false;
  let settledUpdateRequested = false;
  let settledFollowupsRemaining = 1;
  let updateRequested = false;
  let updatesRemaining = MAX_FEEDBACK_UPDATES;
  let isOpen = false;
  let lastHeight = null;
  let lastTop = null;

  function applyViewport() {
    if (!isOpen || !viewport) return;

    const height = Math.round(viewport.height);
    const top = Math.round(viewport.offsetTop);
    const heightChanged = lastHeight !== height;
    const topChanged = lastTop !== top;

    if (!heightChanged && !topChanged) return false;

    lastHeight = height;
    lastTop = top;
    pmContainer.style.setProperty('height', height + 'px', 'important');
    pmContainer.style.setProperty('top', top + 'px', 'important');
    scrollToBottom();
    return true;
  }

  function scheduleSettledUpdate() {
    if (settleTimer !== null) browserWindow.clearTimeout(settleTimer);
    settleTimer = browserWindow.setTimeout(function() {
      settleTimer = null;
      if (!isOpen || framePending) {
        if (isOpen) scheduleSettledUpdate();
        return;
      }

      settling = true;
      settledUpdateRequested = false;
      try {
        applyViewport();
      } finally {
        settling = false;
        if (settledUpdateRequested && settledFollowupsRemaining > 0) {
          settledFollowupsRemaining -= 1;
          scheduleSettledUpdate();
        }
      }
    }, SETTLE_DELAY_MS);
  }

  function queueViewportUpdate() {
    if (!isOpen || !viewport || updatesRemaining === 0) return;

    if (framePending) {
      updateRequested = true;
      return;
    }

    framePending = true;
    updateRequested = false;
    frameId = browserWindow.requestAnimationFrame(function() {
      try {
        if (applyViewport()) updatesRemaining -= 1;
      } finally {
        const needsFollowup = updateRequested && updatesRemaining > 0;
        framePending = false;
        frameId = null;
        if (needsFollowup) queueViewportUpdate();
      }
    });
  }

  function scheduleViewportUpdate() {
    if (!isOpen || !viewport) return;
    if (settling) {
      settledUpdateRequested = true;
      return;
    }
    if (updatesRemaining === 0 && settleTimer === null) {
      if (settledFollowupsRemaining === 0) return;
      settledFollowupsRemaining -= 1;
    }
    scheduleSettledUpdate();
    queueViewportUpdate();
  }

  function open() {
    isOpen = true;
    updatesRemaining = MAX_FEEDBACK_UPDATES;
    settledFollowupsRemaining = 1;
    settledUpdateRequested = false;
    pmContainer.classList.add('keyboard-open');

    if (viewport) {
      viewport.addEventListener('resize', scheduleViewportUpdate);
      viewport.addEventListener('scroll', scheduleViewportUpdate);
      scheduleViewportUpdate();
    }

    if (scrollTimer !== null) browserWindow.clearTimeout(scrollTimer);
    scrollTimer = browserWindow.setTimeout(function() {
      scrollTimer = null;
      scrollToBottom();
    }, 300);
  }

  function close() {
    isOpen = false;
    pmContainer.classList.remove('keyboard-open');

    if (framePending) {
      browserWindow.cancelAnimationFrame(frameId);
      framePending = false;
      frameId = null;
    }
    updateRequested = false;
    settledUpdateRequested = false;
    if (settleTimer !== null) {
      browserWindow.clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (scrollTimer !== null) {
      browserWindow.clearTimeout(scrollTimer);
      scrollTimer = null;
    }

    if (viewport) {
      viewport.removeEventListener('resize', scheduleViewportUpdate);
      viewport.removeEventListener('scroll', scheduleViewportUpdate);
    }

    pmContainer.style.removeProperty('height');
    pmContainer.style.removeProperty('top');
    lastHeight = null;
    lastTop = null;
  }

  return { open, close };
}
